import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { IconModule, OneSecondIntervalService, TimeDeltaService } from '@hglobal/common';
import { SurveyStatus, VoteResults } from '@pt/models';
import { VoteQuestionType } from '@hglobal/surveys';
import { TranslateModule } from '@ngx-translate/core';

import {
  VOTE_QUESTIONS_WITH_IMAGE,
  VOTE_QUESTIONS_WITH_MULTIPLE_ANSWERS,
} from './vote-results.constants';

interface MedalPositions {
  gold: number[];
  silver: number[];
  bronze: number[];
}

@Component({
  selector: 'pt-vote-results',
  templateUrl: './vote-results.component.html',
  styleUrl: './vote-results.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconModule,
    TranslateModule,
  ],
})
export class VoteResultsComponent {
  #timeDeltaService = inject(TimeDeltaService);
  #interval$ = inject(OneSecondIntervalService);

  results = input.required<VoteResults>();
  questionType = input.required<VoteQuestionType>();
  voteStatus = input.required<SurveyStatus>();
  endDate = input.required<string>();
  displaySelected = input<boolean, unknown>(false, { transform: booleanAttribute });

  sortedOptions = computed(() => {
    const options = this.results()?.options || [];
    return [...options].sort((a, b) => b.answersCount - a.answersCount);
  });

  isCheckbox = computed(() => this.withCheckbox.includes(this.questionType()));
  isWithImage = computed(() => this.withImage.includes(this.questionType()));

  gridClasses = computed(() => {
    const classes = ['pt-vote-results__options'];

    let columns = 2;

    if (this.isWithImage()) {
      columns++;
    }

    if (this.displaySelected()) {
      columns++;
    }

    classes.push(`pt-vote-results__options-${columns}-columns`);

    return classes;
  });

  readonly withCheckbox = VOTE_QUESTIONS_WITH_MULTIPLE_ANSWERS;
  readonly withImage = VOTE_QUESTIONS_WITH_IMAGE;
  readonly #currentTime = toSignal(this.#interval$, { initialValue: Date.now() });

  medalPositions = computed<MedalPositions>(() => {
    const sortedOptions = this.sortedOptions();

    if (sortedOptions.length === 0 || this.results().answersCount === 0) {
      return { gold: [], silver: [], bronze: [] };
    }

    const positions: MedalPositions = {
      gold: [],
      silver: [],
      bronze: [],
    };

    // Get gold positions
    const goldVoteCount = sortedOptions[0].answersCount;
    for (let i = 0; i < sortedOptions.length; i++) {
      if (sortedOptions[i].answersCount === goldVoteCount) {
        positions.gold.push(i);
      } else {
        break;
      }
    }

    // If we have more than 4 options, get silver positions
    if (sortedOptions.length > 4 && positions.gold.length < sortedOptions.length) {
      const silverVoteCount = sortedOptions[positions.gold.length].answersCount;

      if (silverVoteCount === 0) {
        return positions;
      }

      for (let i = positions.gold.length; i < sortedOptions.length; i++) {
        if (sortedOptions[i].answersCount === silverVoteCount) {
          positions.silver.push(i);
        } else {
          break;
        }
      }
    }

    // If we have more than 6 options, get bronze positions
    if (
      sortedOptions.length > 6
      && positions.gold.length + positions.silver.length < sortedOptions.length
    ) {
      const bronzeVoteCount = sortedOptions[
        positions.gold.length + positions.silver.length
      ].answersCount;

      if (bronzeVoteCount === 0) {
        return positions;
      }

      for (let i = positions.gold.length + positions.silver.length; i < sortedOptions.length; i++) {
        if (sortedOptions[i].answersCount === bronzeVoteCount) {
          positions.bronze.push(i);
        } else {
          break;
        }
      }
    }

    return positions;
  });

  timeLeft = computed(() => {
    if (this.voteStatus() === SurveyStatus.FINISHED) {
      return null;
    }

    const now = this.#currentTime() + Date.now() + this.#timeDeltaService.value;
    const endTime = Date.parse(this.endDate() + ' ') + (24 * 60 * 60 * 1000);
    const delta = endTime - now;

    if (delta <= 0) {
      return null;
    }

    const hours = Math.floor(delta / (1000 * 60 * 60))
      .toString()
      .padStart(2, '0');
    const minutes = Math.floor((delta % (1000 * 60 * 60)) / (1000 * 60))
      .toString()
      .padStart(2, '0');
    const seconds = Math.floor((delta % (1000 * 60)) / 1000)
      .toString()
      .padStart(2, '0');

    return `${hours}:${minutes}:${seconds}`;
  });
}
